import React, { FunctionComponent, useState } from "react";
import { MosaicElementType, WeatherMultiCityParameters, WidgetLayoutOrientation } from "../../../../api/mosaicApi";
import { Button } from "../../../components/Button";
import { FormInput, FormInputColorpicker, FormInputNumber, FormRow, FormSelect, SelectOption } from "../../../components/formRow/FormRow";
import { SelectAvatarButton } from "../../../components/selectFileButton/SelectFileButton";
import SvgSelector from "../../../components/svgSelector/SvgSelector";
import FormCheckbox from "../../../components/formRow/FormCheckbox";
import IconButton from "../../../components/iconButton/IconButton";
import { BaseWidgetData, WidgetPositionProps, usePositionHandlers, useImageUpload, makeNumericHandler } from "./widgetShared";
import { WidgetPositionControls } from "./WidgetPositionControls";

export type WeatherMultiCityWidgetData = BaseWidgetData<WeatherMultiCityParameters>;

interface WeatherMultiCityWidgetProps extends WidgetPositionProps {
    onAddWidget: (data: WeatherMultiCityWidgetData) => void;
    onBackgroundImageUrlChange?: (url: string) => void;
}

const MAX_CITIES = 8;

const OrientationOptions: SelectOption[] = [
    { name: 'Horizontal', value: '0' },
    { name: 'Vertical', value: '1' }
];

export const WeatherMultiCityWidget: FunctionComponent<WeatherMultiCityWidgetProps> = (props) => {
    const {
        columnStart, width, rowStart, height,
        onColumnStartChange, onWidthChange, onRowStartChange, onHeightChange,
        isOutOfLimit, maxElements, onAddWidget, validationError,
        onBackgroundImageUrlChange
    } = props;

    const [noBackground, setNoBackground] = useState<boolean>(false);
    const [cities, setCities] = useState<string[]>([]);
    const [currentCity, setCurrentCity] = useState<string>('');
    const [updateIntervalMinutes, setUpdateIntervalMinutes] = useState<number | string>(30);
    const [orientation, setOrientation] = useState<number>(WidgetLayoutOrientation.Horizontal);
    const [textColour, setTextColour] = useState<string>('#ffffff');
    const [backgroundColour, setBackgroundColour] = useState<string>('#000000');
    const [cityNameFontSize, setCityNameFontSize] = useState<number | string>('');
    const [weatherDescriptionFontSize, setWeatherDescriptionFontSize] = useState<number | string>('');
    const [currentTemperatureFontSize, setCurrentTemperatureFontSize] = useState<number | string>('');
    const [windFontSize, setWindFontSize] = useState<number | string>('');
    const [currentIconSize, setCurrentIconSize] = useState<number | string>('');
    const [leftPadding, setLeftPadding] = useState<number | string>('');
    const [spaceBetweenCities, setSpaceBetweenCities] = useState<number | string>('');

    const positionHandlers = usePositionHandlers({
        onColumnStartChange, onWidthChange, onRowStartChange, onHeightChange
    });

    const bgUpload = useImageUpload({
        onUrlChange: onBackgroundImageUrlChange,
    });

    // City handlers
    const handleCurrentCityChange = (e: React.ChangeEvent<HTMLInputElement>) => setCurrentCity(e.target.value);

    const addCity = () => {
        if (currentCity.trim() && cities.length < MAX_CITIES) {
            setCities([...cities, currentCity.trim()]);
            setCurrentCity('');
        }
    };

    const removeCity = (index: number) => {
        setCities(cities.filter((_, i) => i !== index));
    };

    const handleCityKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addCity();
        }
    };

    const handleOrientationChange = (value: string) => setOrientation(+value);

    const resetState = () => {
        setCities([]);
        setCurrentCity('');
        setUpdateIntervalMinutes(30);
        setOrientation(WidgetLayoutOrientation.Horizontal);
        bgUpload.reset();
        setNoBackground(false);
        setTextColour('#ffffff');
        setBackgroundColour('#000000');
        setCityNameFontSize('');
        setWeatherDescriptionFontSize('');
        setCurrentTemperatureFontSize('');
        setWindFontSize('');
        setCurrentIconSize('');
        setLeftPadding('');
        setSpaceBetweenCities('');
    };

    const handleAddWidget = () => {
        const parameters: WeatherMultiCityParameters = {
            type: "weatherMultiCity",
            cities: cities,
            updateIntervalMinutes: updateIntervalMinutes === '' ? undefined : +updateIntervalMinutes,
            orientation: orientation,
            noBackground: noBackground || undefined,
            backgroundImageRecourceBlobName: bgUpload.imageName || undefined,
            backgroundImageUrl: bgUpload.imageUrl || undefined,
            textColour: textColour,
            backgroundColour: backgroundColour,
            cityNameFontSize: cityNameFontSize === '' ? undefined : +cityNameFontSize,
            weatherDescriptionFontSize: weatherDescriptionFontSize === '' ? undefined : +weatherDescriptionFontSize,
            currentTemperatureFontSize: currentTemperatureFontSize === '' ? undefined : +currentTemperatureFontSize,
            windFontSize: windFontSize === '' ? undefined : +windFontSize,
            currentIconSize: currentIconSize === '' ? undefined : +currentIconSize,
            leftPadding: leftPadding === '' ? undefined : +leftPadding,
            spaceBetweenCities: spaceBetweenCities === '' ? undefined : +spaceBetweenCities,
        };

        onAddWidget({
            type: MosaicElementType.WeatherMultiCity,
            startColumn: +columnStart,
            width: +width,
            startRow: +rowStart,
            height: +height,
            name: cities.join(', '),
            parameters: parameters
        });

        resetState();
    };

    return (
        <>
            <div style={{ minHeight: '180px' }}>
                <WidgetPositionControls
                    idPrefix="weather-mc"
                    columnStart={columnStart}
                    width={width}
                    rowStart={rowStart}
                    height={height}
                    handlers={positionHandlers}
                    isOutOfLimit={isOutOfLimit}
                    maxElements={maxElements}
                    validationError={validationError}
                />

                {!isOutOfLimit && (
                    <div className="weather-multi-city-widget" style={{ display: 'block' }}>
                        {/* Cities */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Cities (max {MAX_CITIES})</h4>
                        <div className="d-flex gap15 align-items-center" style={{ width: '100%' }}>
                            <div style={{ flex: 4 }}>
                                <FormRow>
                                    <FormInput
                                        errorMessage="Please enter city"
                                        id="weather_mc_city"
                                        isError={false}
                                        title="City"
                                        onChange={handleCurrentCityChange}
                                        value={currentCity}
                                        placeholderTxt="Enter city name"
                                    />
                                </FormRow>
                            </div>
                            <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    color="green"
                                    label="Add City"
                                    onClick={addCity}
                                    isDisabled={!currentCity.trim() || cities.length >= MAX_CITIES}
                                />
                            </div>
                        </div>
                        {cities.length > 0 && (
                            <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                {cities.map((city, index) => (
                                    <div key={index} className="d-flex align-items-center" style={{ marginBottom: '5px' }}>
                                        <span style={{ marginRight: '10px' }}>{index + 1}. {city}</span>
                                        <IconButton tooltipText="Remove" type="cross" onClick={() => removeCity(index)} />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Basic Settings */}
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormSelect
                                    id="weather_mc_orientation"
                                    title="Orientation"
                                    selectedValue={orientation.toString()}
                                    isError={false}
                                    errorMessage="Error"
                                    options={OrientationOptions}
                                    onChange={handleOrientationChange}
                                    hideDefaultValue={true}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_update_interval"
                                    title="Update Interval (min)"
                                    isError={false}
                                    errorMessage="Please enter update interval"
                                    onChangeValue={makeNumericHandler(setUpdateIntervalMinutes)}
                                    onChange={() => {}}
                                    value={updateIntervalMinutes}
                                />
                            </FormRow>
                        </div>

                        {/* Background Image */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Background</h4>
                        <div className="d-flex gap15" style={{ flexWrap: 'wrap', marginBottom: '10px' }}>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_mc_no_background" checked={noBackground} onChange={setNoBackground} />
                                <span style={{ marginLeft: '8px' }}>No Background</span>
                            </div>
                        </div>
                        {!noBackground && (
                        <div className="d-flex gap15 align-items-center">
                            <FormRow>
                                <SelectAvatarButton onChange={bgUpload.handleFileChange} id="weather-background-multy" title="Upload Background"/>
                            </FormRow>
                            {bgUpload.imageUrl && (
                                <img
                                    className="mosaic-img"
                                    src={bgUpload.imageUrl}
                                    alt="Background preview"
                                    style={{ maxHeight: '100px', width: 'auto', objectFit: 'contain' }}
                                />
                            )}
                        </div>
                        )}

                        {/* Colors */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Colors</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputColorpicker
                                    id="weather_mc_text_colour"
                                    title="Text Color"
                                    isError={false}
                                    errorMessage="Please enter text color"
                                    onChangeValue={(value: string) => setTextColour(value)}
                                    onChange={() => {}}
                                    value={textColour}
                                />
                            </FormRow>
                            {!noBackground && (
                            <FormRow>
                                <FormInputColorpicker
                                    id="weather_mc_background_colour"
                                    title="Background Color"
                                    isError={false}
                                    errorMessage="Please enter background color"
                                    onChangeValue={(value: string) => setBackgroundColour(value)}
                                    onChange={() => {}}
                                    value={backgroundColour}
                                />
                            </FormRow>
                            )}
                        </div>

                        {/* Font Sizes */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Font Sizes</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_city_name_font_size"
                                    title="City Name"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setCityNameFontSize)}
                                    onChange={() => {}}
                                    value={cityNameFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_description_font_size"
                                    title="Description"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setWeatherDescriptionFontSize)}
                                    onChange={() => {}}
                                    value={weatherDescriptionFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_current_temp_font_size"
                                    title="Temperature"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setCurrentTemperatureFontSize)}
                                    onChange={() => {}}
                                    value={currentTemperatureFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_wind_font_size"
                                    title="Wind"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setWindFontSize)}
                                    onChange={() => {}}
                                    value={windFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_current_icon_size"
                                    title="Icon Size"
                                    isError={false}
                                    errorMessage="Please enter icon size"
                                    onChangeValue={makeNumericHandler(setCurrentIconSize)}
                                    onChange={() => {}}
                                    value={currentIconSize}
                                />
                            </FormRow>
                        </div>

                        {/* Padding & Spacing */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Padding & Spacing</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_left_padding"
                                    title="Left Padding"
                                    isError={false}
                                    errorMessage="Please enter padding"
                                    onChangeValue={makeNumericHandler(setLeftPadding)}
                                    onChange={() => {}}
                                    value={leftPadding}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_mc_space_between_cities"
                                    title="Space Between Cities"
                                    isError={false}
                                    errorMessage="Please enter spacing"
                                    onChangeValue={makeNumericHandler(setSpaceBetweenCities)}
                                    onChange={() => {}}
                                    value={spaceBetweenCities}
                                />
                            </FormRow>
                        </div>
                    </div>
                )}
            </div>
            <div className="mosaic-settings-add-elem align-items-center" style={{ marginTop: '20px', marginBottom: '15px' }}>
                <Button
                    color="green"
                    label="Add Multi-City Weather Widget"
                    onClick={handleAddWidget}
                    isDisabled={isOutOfLimit || cities.length === 0}
                />
            </div>
        </>
    );
};

// Helper functions for Weather Multi-City widget
export const getWeatherMultiCityWidgetIcon = () => <SvgSelector title="weather" />;
export const getWeatherMultiCityWidgetName = () => 'Weather Multi-City';
export const getWeatherMultiCityWidgetLimit = () => 3;

export default WeatherMultiCityWidget;
